import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { supabase } from '../../lib/supabaseClient'

// Helper function to calculate total amount
const calculateTotalAmount = (items) => {
  return items.reduce((total, item) => total + item.totalPrice, 0);
};

// Get initial state from localStorage if it exists
const loadCartFromStorage = () => {
  if (typeof window === 'undefined') {
    return {
      items: [],
      totalQuantity: 0,
      totalAmount: 0,
      userId: null,
      isLoading: false
    };
  }
  
  try {
    // First try to load user-specific cart if userId exists in localStorage
    const userId = localStorage.getItem('userId');
    if (userId) {
      const userCart = localStorage.getItem(`cart_${userId}`);
      if (userCart) {
        const parsedCart = JSON.parse(userCart);
        const items = parsedCart.items || parsedCart || [];
        return {
          items: items,
          totalQuantity: items.reduce((total, item) => total + item.quantity, 0),
          totalAmount: calculateTotalAmount(items),
          userId: userId,
          isLoading: false
        };
      }
    }
    
    // If no user cart, try to load guest cart
    const guestCart = localStorage.getItem('cart');
    if (guestCart) {
      const parsedCart = JSON.parse(guestCart);
      const items = parsedCart.items || [];
      return {
        items: items,
        totalQuantity: items.reduce((total, item) => total + item.quantity, 0),
        totalAmount: calculateTotalAmount(items),
        userId: null,
        isLoading: false
      };
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  
  return {
    items: [],
    totalQuantity: 0,
    totalAmount: 0,
    userId: null,
    isLoading: false
  };
};

// Initial state
const initialState = loadCartFromStorage();

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (userId, { getState }) => {
    const state = getState();
    if (state.cart.isLoading) {
      return null; // Skip if already loading
    }

    try {
      console.log('Fetching cart for user:', userId);
      
      // Try to load from localStorage first
      const cachedCart = localStorage.getItem(`cart_${userId}`);
      if (cachedCart) {
        console.log('Loaded cart from localStorage cache');
        return JSON.parse(cachedCart);
      }
      
      // If no cache or cache is invalid, fetch from Supabase
      const { data: cartData, error } = await supabase
        .from('carts')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) throw error;
      
      if (cartData) {
        // Cache the result
        localStorage.setItem(`cart_${userId}`, JSON.stringify(cartData));
        console.log('Successfully loaded cart from Supabase');
        return cartData;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }
);

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCart: (state, action) => {
      const { items, userId } = action.payload;
      state.items = items || [];
      state.userId = userId;
      state.totalQuantity = items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = calculateTotalAmount(items);
      
      // Save to appropriate storage
      if (userId) {
        saveCartToSupabase(userId, items);
      } else {
        localStorage.setItem('cart', JSON.stringify({ items, totalQuantity: state.totalQuantity, totalAmount: state.totalAmount }));
      }
    },
    addToCart: (state, action) => {
      const newItem = action.payload;
      const existingItemIndex = state.items.findIndex(
        item => item.id === newItem.id && item.size === newItem.size &&
        JSON.stringify(item.customizations) === JSON.stringify(newItem.customizations) &&
        item.badge === newItem.badge
      );
      
      if (existingItemIndex !== -1) {
        state.items[existingItemIndex].quantity += newItem.quantity;
        state.items[existingItemIndex].totalPrice += newItem.totalPrice;
      } else {
        state.items.push(newItem);
      }
      
      state.totalQuantity = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = calculateTotalAmount(state.items);
      
      // Save to appropriate storage
      if (state.userId) {
        saveCartToSupabase(state.userId, state.items);
      } else {
        localStorage.setItem('cart', JSON.stringify({ 
          items: state.items,
          totalQuantity: state.totalQuantity,
          totalAmount: state.totalAmount
        }));
      }
    },
    updateCartItemQuantity: (state, action) => {
      const { id, size, quantity, customizations, badge } = action.payload;
      
      // Find item by id, size, AND matching customizations and badge
      const existingItemIndex = state.items.findIndex(item => {
        return item.id === id && 
               item.size === size && 
               JSON.stringify(item.customizations) === JSON.stringify(customizations) &&
               item.badge === badge;
      });
      
      if (existingItemIndex !== -1) {
        // Calculate new total price based on unit price
        const unitPrice = state.items[existingItemIndex].totalPrice / state.items[existingItemIndex].quantity;
        
        // Update quantity and total price
        state.items[existingItemIndex].quantity = quantity;
        state.items[existingItemIndex].totalPrice = unitPrice * quantity;
        
        // Update total quantity and amount
        state.totalQuantity = state.items.reduce((total, item) => total + item.quantity, 0);
        state.totalAmount = calculateTotalAmount(state.items);
        
        // Save to appropriate storage
        if (state.userId) {
          saveCartToSupabase(state.userId, state.items);
        } else {
          localStorage.setItem('cart', JSON.stringify({ 
            items: state.items,
            totalQuantity: state.totalQuantity,
            totalAmount: state.totalAmount
          }));
        }
      }
    },
    removeFromCart: (state, action) => {
      const { id, size, customizations, badge } = action.payload;
      
      // Find item by id, size, AND matching customizations and badge
      const existingItemIndex = state.items.findIndex(item => {
        return item.id === id && 
               item.size === size && 
               JSON.stringify(item.customizations) === JSON.stringify(customizations) &&
               item.badge === badge;
      });
      
      if (existingItemIndex !== -1) {
        // Remove item from cart
        state.items.splice(existingItemIndex, 1);
        
        // Update total quantity and amount
        state.totalQuantity = state.items.reduce((total, item) => total + item.quantity, 0);
        state.totalAmount = calculateTotalAmount(state.items);
        
        // Save to appropriate storage
        if (state.userId) {
          saveCartToSupabase(state.userId, state.items);
        } else {
          localStorage.setItem('cart', JSON.stringify({ 
            items: state.items,
            totalQuantity: state.totalQuantity,
            totalAmount: state.totalAmount
          }));
        }
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      
      // Clear from appropriate storage
      if (state.userId) {
        // For logged-in users, clear from Supabase asynchronously
        saveCartToSupabase(state.userId, []);
      } else {
        // For guests, clear from localStorage
        localStorage.removeItem('cart');
      }
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.items = action.payload.items || [];
          state.totalAmount = action.payload.total_amount || 0;
        }
      })
      .addCase(fetchCart.rejected, (state) => {
        state.isLoading = false;
      });
  }
})

// Helper function to save cart to Supabase
const saveCartToSupabase = async (userId, items) => {
  if (!userId) return;
  
  try {
    console.log("Saving cart for user:", userId);
    
    // Save to localStorage first as a backup
    const cartState = {
      items: items,
      totalQuantity: items.reduce((total, item) => total + item.quantity, 0),
      totalAmount: calculateTotalAmount(items)
    };
    localStorage.setItem(`cart_${userId}`, JSON.stringify(cartState));
    
    // Check if the user already has a cart
    const { data: existingCarts, error: checkError } = await supabase
      .from('user_carts')
      .select('id')
      .eq('user_id', userId);
      
    if (checkError) {
      console.error("Error checking cart existence:", checkError);
      return; // Already saved to localStorage above
    }
    
    if (existingCarts && existingCarts.length > 0) {
      // Update existing cart
      const cartId = existingCarts[0].id;
      
      const { error: updateError } = await supabase
        .from('user_carts')
        .update({
          cart_data: JSON.stringify(cartState),
          updated_at: new Date().toISOString()
        })
        .eq('id', cartId);
        
      if (updateError) {
        console.error('Error updating cart in Supabase:', updateError);
      }
    } else {
      // Create new cart
      const { error: insertError } = await supabase
        .from('user_carts')
        .insert({
          user_id: userId,
          cart_data: JSON.stringify(cartState)
        });
        
      if (insertError) {
        console.error('Error creating cart in Supabase:', insertError);
      }
    }
  } catch (error) {
    console.error('Error saving cart to Supabase:', error);
  }
};

export const { addToCart, updateCartItemQuantity, removeFromCart, clearCart, setCart, setUserId } = cartSlice.actions

// Thunk to merge guest cart with user cart on login
export const mergeGuestCartWithUserCart = (userId) => async (dispatch, getState) => {
  if (!userId) return;
  
  try {
    const { cart } = getState();
    let mergedItems = [];
    let userCartItems = [];
    let apiSuccessful = true;
    
    // If there are items in the guest cart
    if (cart.items.length > 0 && !cart.userId) {
      mergedItems = [...cart.items];
      
      // Try to fetch the existing user cart from Supabase
      try {
        const { data, error } = await supabase
          .from('user_carts')
          .select('cart_data')
          .eq('user_id', userId)
          .single();
          
        if (!error && data && data.cart_data) {
          // Parse the existing user cart
          userCartItems = JSON.parse(data.cart_data);
        } else if (error && error.code !== 'PGRST116') {
          console.error('Error fetching user cart during merge:', error);
          apiSuccessful = false;
          
          // Try to get cart from localStorage fallback
          try {
            const localCart = localStorage.getItem(`cart_${userId}`);
            if (localCart) {
              console.log('Using user cart from localStorage fallback for merge');
              userCartItems = JSON.parse(localCart);
            }
          } catch (localStorageError) {
            console.error('Error reading from localStorage:', localStorageError);
          }
        }
      } catch (fetchError) {
        console.error('Error fetching user cart:', fetchError);
        apiSuccessful = false;
        
        // Try to get cart from localStorage fallback
        try {
          const localCart = localStorage.getItem(`cart_${userId}`);
          if (localCart) {
            console.log('Using user cart from localStorage fallback for merge after error');
            userCartItems = JSON.parse(localCart);
          }
        } catch (localStorageError) {
          console.error('Error reading from localStorage:', localStorageError);
        }
      }
      
      // Combine items, checking for duplicates
      userCartItems.forEach(userItem => {
        const existingItemIndex = mergedItems.findIndex(
          item => item.id === userItem.id && 
                 item.size === userItem.size &&
                 JSON.stringify(item.customizations) === JSON.stringify(userItem.customizations) &&
                 item.badge === userItem.badge
        );
        
        if (existingItemIndex !== -1) {
          // Update quantity and price for existing items
          mergedItems[existingItemIndex].quantity += userItem.quantity;
          mergedItems[existingItemIndex].totalPrice += userItem.totalPrice;
        } else {
          // Add new items
          mergedItems.push(userItem);
        }
      });
      
      // Try to save the merged cart to Supabase
      if (apiSuccessful) {
        try {
          await saveCartToSupabase(userId, mergedItems);
        } catch (saveError) {
          console.error('Error saving merged cart to Supabase:', saveError);
          // Save to localStorage as fallback
          localStorage.setItem(`cart_${userId}`, JSON.stringify(mergedItems));
        }
      } else {
        // Save directly to localStorage if API was not successful
        localStorage.setItem(`cart_${userId}`, JSON.stringify(mergedItems));
      }
      
      // Update Redux state
      dispatch(setCart({ items: mergedItems, userId }));
      
      // Clear localStorage cart
      localStorage.removeItem('cart');
    } else {
      // Just fetch the user's cart from the server
      dispatch(fetchCart(userId));
    }
  } catch (error) {
    console.error('Error merging carts:', error);
    // Attempt to restore from localStorage as last resort
    try {
      const localCart = localStorage.getItem(`cart_${userId}`);
      if (localCart) {
        const items = JSON.parse(localCart);
        dispatch(setCart({ items, userId }));
      }
    } catch (localStorageError) {
      console.error('Error reading from localStorage during error recovery:', localStorageError);
    }
  }
};

export default cartSlice.reducer